<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-plus-circle me-2 text-primary"></i>
            Gestione Aggiunte e Supplementi
          </h1>
          <p class="sci-subtitle">Configura quantità predefinite e prezzi supplementari per le aggiunte ordinate ai tavoli</p>
        </div>
      </div>
      <div class="sci-header-right">
        <span class="sci-badge sci-badge-count">
          {{ filteredProdotti.length }} di {{ prodotti.length }} ingredienti/prodotti
        </span>
      </div>
    </header>

    <!-- Main Content -->
    <main class="sci-main-content">
      <!-- Search Toolbar -->
      <div class="sci-toolbar-card">
        <div class="sci-search-group">
          <i class="fas fa-search search-icon"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Cerca ingrediente o prodotto per nome o codice..." 
            class="sci-search-input"
          />
          <button 
            v-if="searchQuery" 
            class="clear-search-btn" 
            title="Azzera ricerca"
            @click="searchQuery = ''"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button class="sci-btn sci-btn-secondary" @click="displayProduct" :disabled="isLoading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          <span>Ricarica</span>
        </button>
      </div>

      <!-- Additions Table Card -->
      <section class="sci-table-card">
        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th>Ingrediente / Prodotto</th>
                <th style="width: 220px;">Quantità Standard</th>
                <th style="width: 240px;">Prezzo Supplemento (€)</th>
                <th style="width: 120px; text-align: center;">Stato</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="prodotto in filteredProdotti" :key="prodotto.code" class="sci-table-row">
                <!-- Product / Ingredient Name -->
                <td>
                  <div class="product-cell">
                    <div class="product-avatar">
                      <i class="fas fa-utensils"></i>
                    </div>
                    <div class="product-info">
                      <span class="product-name">{{ prodotto.descrizio || prodotto.nome || 'Senza Nome' }}</span>
                      <span v-if="prodotto.code" class="product-code font-monospace">Cod: {{ prodotto.code }}</span>
                    </div>
                  </div>
                </td>

                <!-- Added Quantity -->
                <td>
                  <div class="sci-input-stepper">
                    <button 
                      class="stepper-btn" 
                      @click="decrementQty(prodotto)"
                      :disabled="prodotto.quantitaAggiunta <= 0"
                    >
                      <i class="fas fa-minus"></i>
                    </button>
                    <input 
                      type="number" 
                      min="0" 
                      step="1"
                      v-model.number="prodotto.quantitaAggiunta" 
                      class="stepper-input font-monospace"
                      @change="handleQtyChange(prodotto)"
                    />
                    <button 
                      class="stepper-btn" 
                      @click="incrementQty(prodotto)"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                  </div>
                </td>

                <!-- Extra Price (€) -->
                <td>
                  <div class="sci-currency-group">
                    <span class="currency-prefix">€</span>
                    <input 
                      type="number" 
                      min="0" 
                      step="0.10"
                      v-model.number="prodotto.prezzoAggiunta" 
                      placeholder="0.00"
                      class="sci-currency-input font-monospace"
                      @change="handlePriceChange(prodotto)"
                    />
                  </div>
                </td>

                <!-- Status indicator -->
                <td style="text-align: center;">
                  <span v-if="savingItemCode === prodotto.code" class="status-saving">
                    <i class="fas fa-spinner fa-spin"></i>
                  </span>
                  <span v-else-if="savedItemCode === prodotto.code" class="status-saved">
                    <i class="fas fa-check-circle"></i> Salvato
                  </span>
                  <span v-else-if="prodotto.prezzoAggiunta > 0 || prodotto.quantitaAggiunta > 0" class="status-active">
                    <i class="fas fa-circle-notch"></i> Attivo
                  </span>
                  <span v-else class="status-idle">
                    —
                  </span>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredProdotti.length === 0">
                <td colspan="4" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-search"></i>
                    </div>
                    <h3 class="empty-title">Nessun ingrediente trovato</h3>
                    <p class="empty-desc">
                      {{ searchQuery ? 'Nessun prodotto corrisponde alla ricerca inserita.' : 'Non ci sono ancora prodotti mappati nel catalogo.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Aggiunte',
  data() {
    return {
      prodotti: [],
      searchQuery: '',
      isLoading: false,
      savingItemCode: null,
      savedItemCode: null,
      store
    }
  },
  computed: {
    filteredProdotti() {
      if (!this.prodotti) return [];
      const query = (this.searchQuery || '').toLowerCase().trim();
      if (!query) return this.prodotti;

      return this.prodotti.filter(prodotto => {
        const desc = (prodotto.descrizio || '').toLowerCase();
        const nome = (prodotto.nome || '').toLowerCase();
        const code = (prodotto.code || '').toLowerCase();
        return desc.includes(query) || nome.includes(query) || code.includes(query);
      });
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" })
    },
    displayProduct() {
      this.isLoading = true;
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;
      this.axios.post(this.store.path + 'displayProduct', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        const dataList = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.messaggio ? response.data.messaggio : []);

        if (dataList.length > 0) {
          const list = [];
          dataList.forEach(element => {
            element.prezzo = element.prezzo ? element.prezzo : 0;
            element.quantitamin = element.quantitamin ? element.quantitamin : 0;
            element.quantitaordine = element.quantitaordine ? element.quantitaordine : 0;
            element.stato = element.stato ? element.stato : '';
            element.quantitaAggiunta = element.quantitaAggiunta ? Number(element.quantitaAggiunta) : 0;
            element.prezzoAggiunta = element.prezzoAggiunta ? Number(element.prezzoAggiunta) : 0;
            list.push(element);
          });
          this.prodotti = list;
        }
      }).catch(err => {
        console.error('Errore caricamento prodotti:', err);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    incrementQty(prodotto) {
      prodotto.quantitaAggiunta = (prodotto.quantitaAggiunta || 0) + 1;
      this.handleQtyChange(prodotto);
    },
    decrementQty(prodotto) {
      if (prodotto.quantitaAggiunta > 0) {
        prodotto.quantitaAggiunta = prodotto.quantitaAggiunta - 1;
        this.handleQtyChange(prodotto);
      }
    },
    handleQtyChange(prodotto) {
      this.updateQuantita(
        prodotto.code,
        null,
        null,
        null,
        prodotto.quantitaAggiunta,
        null
      );
    },
    handlePriceChange(prodotto) {
      this.updateQuantita(
        prodotto.code,
        null,
        null,
        null,
        null,
        prodotto.prezzoAggiunta
      );
    },
    updateQuantita(code, quantita, quantitamin, quantitaordine, quantitaAggiunta, prezzoAggiunta) {
      this.savingItemCode = code;
      this.axios.post(this.store.path + 'updateQuantita', {
        code: code,
        quantita: quantita,
        quantitamin: quantitamin,
        quantitaordine: quantitaordine,
        quantitaAggiunta: quantitaAggiunta,
        prezzoAggiunta: prezzoAggiunta,
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).then(() => {
        this.savingItemCode = null;
        this.savedItemCode = code;
        setTimeout(() => {
          if (this.savedItemCode === code) {
            this.savedItemCode = null;
          }
        }, 2000);
      }).catch(err => {
        this.savingItemCode = null;
        console.error('Errore durante salvataggio quantita/prezzo:', err);
      });
    }
  },
  beforeMount() {
    this.displayProduct();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.sci-badge-count {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
}

/* Main Content */
.sci-main-content {
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Toolbar */
.sci-toolbar-card {
  display: flex;
  gap: 16px;
  align-items: center;
}

.sci-search-group {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 16px;
  color: #94a3b8;
  font-size: 1rem;
}

.sci-search-input {
  width: 100%;
  min-height: 46px;
  padding: 10px 42px 10px 44px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #0f172a;
  box-shadow: 0 2px 6px rgba(0,0,0,0.03);
  transition: all 0.2s ease;
}

.sci-search-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.clear-search-btn {
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  font-size: 0.9rem;
}

.clear-search-btn:hover {
  color: #ef4444;
}

.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-secondary {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
}

/* Table Card */
.sci-table-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row td {
  padding: 14px 24px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s ease;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.sci-table-row:last-child td {
  border-bottom: none;
}

/* Product Info Cell */
.product-cell {
  display: flex;
  align-items: center;
  gap: 14px;
}

.product-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.product-info {
  display: flex;
  flex-direction: column;
}

.product-name {
  font-size: 0.98rem;
  font-weight: 600;
  color: #1e293b;
}

.product-code {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Stepper Input */
.sci-input-stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.stepper-btn {
  background: #f8fafc;
  border: none;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}

.stepper-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.stepper-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.stepper-input {
  width: 60px;
  height: 38px;
  border: none;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: transparent;
  outline: none;
}

/* Currency Input */
.sci-currency-group {
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 140px;
}

.currency-prefix {
  position: absolute;
  left: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.sci-currency-input {
  width: 100%;
  min-height: 40px;
  padding: 8px 12px 8px 30px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.2s;
}

.sci-currency-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
}

/* Statuses */
.status-saving {
  color: #3b82f6;
  font-size: 1rem;
}

.status-saved {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #059669;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-active {
  color: #10b981;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-idle {
  color: #cbd5e1;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
}
</style>
